import { getResourceStore } from 'store/ResourceStore';
import {
    buildEmptyLayerIdForGeodata,
    buildEmptyLayerIdForSimulationSet,
    EMPTY_SIMULATION_NAME,
    hasRealSimulations,
    EMPTY_SIMULATION_ID,
} from '@data/emptySimulationIdentity';
import { createNewLayerState, DEFAULT_ELEMENT, LayerState, normalizeLayerState, resolveSensorColorMode } from '../layerState';
import { PersistentState } from './persistentStore';

type PersistedPersistentStateSlice = Partial<PersistentState>;

type CleanupResult = {
    state: PersistedPersistentStateSlice;
    notifications: string[];
};

function getCurrentLayerRefs() {
    const resource = getResourceStore();
    const gridResources = resource.status === 'loaded' ? resource.gridResources : null;
    if (!gridResources) return undefined;

    return gridResources.geodatas.flatMap((geodata) =>
        geodata.simulations_sets.length === 0
            ? [
                  {
                      geodataId: geodata.id,
                      layerId: buildEmptyLayerIdForGeodata(geodata.id),
                  },
              ]
            : geodata.simulations_sets.flatMap((simulationsSet) =>
                  hasRealSimulations(simulationsSet.simulations)
                      ? simulationsSet.simulations
                            .filter((subSimulation) => subSimulation.name !== EMPTY_SIMULATION_NAME)
                            .map((subSimulation) => ({
                                geodataId: geodata.id,
                                layerId: `${simulationsSet.id}-${subSimulation.id}`,
                            }))
                      : [
                            {
                                geodataId: geodata.id,
                                layerId: buildEmptyLayerIdForSimulationSet(simulationsSet.id),
                            },
                        ]
              )
    );
}

export function cleanupPersistedState(persistedState: PersistedPersistentStateSlice): CleanupResult {
    const notifications: string[] = [];
    const cleanedState: PersistedPersistentStateSlice = { ...persistedState };

    if (cleanedState.layers) {
        const { cleanedLayers, removedLayerCount, removedIds } = cleanupOldLayers(cleanedState.layers.layerStates);
        if (removedLayerCount > 0) {
            const onlyEmptyLayersRemoved = removedIds.every((id) => id.endsWith(`-${EMPTY_SIMULATION_ID}`));
            if (!onlyEmptyLayersRemoved) {
                notifications.push('Removed persisted layers that are not available in this session.');
            }
        }
        cleanedState.layers.layerStates = cleanedLayers;
        cleanedState.layers.failedLayers = cleanupFailedLayers(cleanedState.layers.failedLayers);

        const normalization = normalizeLayerElements(cleanedState.layers.layerStates);
        cleanedState.layers.layerStates = normalization.cleanedLayers;
        if (normalization.normalizedCount > 0) {
            notifications.push('Fixed incomplete Visualization settings in persisted layers.');
        }

        const activeCleanup = cleanupActiveLayer(cleanedState.layers.activeLayerID, cleanedState.layers.layerStates ?? {});
        cleanedState.layers.activeLayerID = activeCleanup.activeLayerID;
        if (activeCleanup.wasCleared) {
            notifications.push('Active layer was not available and has been cleared.');
        }

        const mixerCleanup = cleanupMixerElements(cleanedState.layers.layerStates ?? {});
        cleanedState.layers.layerStates = mixerCleanup.layers;
        if (mixerCleanup.removedCount > 0) {
            notifications.push('Removed mixer entries for layers that are not available.');
        }
    }

    return { state: cleanedState, notifications };
}

function normalizeLayerElements(layers: Record<string, LayerState> | undefined) {
    if (!layers) return { cleanedLayers: {} as Record<string, LayerState>, normalizedCount: 0 };

    const cleanedLayers: Record<string, LayerState> = {};
    let normalizedCount = 0;
    for (const [id, layer] of Object.entries(layers)) {
        const prevElement = layer.element ?? {};
        const resolvedColorMode = resolveSensorColorMode(prevElement);
        const wasIncomplete = !layer.element || layer.element.SEL_VIS_ELEMENT === undefined;
        const missingColorMode = (prevElement as any).SENSOR_COLOR_MODE === undefined;
        const missingNoStrokes = (prevElement as any).NO_STROKES === undefined;
        const missingVectorialScalarMode = layer.VECTORIAL_SCALAR_MODE === undefined;
        const missingSlice =
            !layer.filter || !layer.projection || !layer.simulationItems || !layer.segments || !layer.data || !layer.performanceMetrics;
        const shouldNormalize = wasIncomplete || missingColorMode || missingNoStrokes || missingVectorialScalarMode || missingSlice;
        if (shouldNormalize) normalizedCount += 1;

        const nextElement = wasIncomplete
            ? { ...DEFAULT_ELEMENT, ...prevElement, SENSOR_COLOR_MODE: resolvedColorMode }
            : missingColorMode || missingNoStrokes
              ? { ...DEFAULT_ELEMENT, ...prevElement, SENSOR_COLOR_MODE: resolvedColorMode }
              : prevElement;

        cleanedLayers[id] = {
            ...normalizeLayerState(layer, layer?.geodataId ?? ''),
            VECTORIAL_SCALAR_MODE: layer.VECTORIAL_SCALAR_MODE ?? 0,
            element: nextElement,
            performanceMetrics: {
                thresholdLevel: resolveThresholdLevel(layer.performanceMetrics),
            },
        };
    }

    return { cleanedLayers, normalizedCount };
}

function resolveThresholdLevel(metrics: unknown): LayerState['performanceMetrics']['thresholdLevel'] {
    const nestedMetrics = (metrics as { metrics?: Record<string, unknown> } | undefined)?.metrics;
    const source = (nestedMetrics ?? metrics) as Record<string, unknown> | undefined;
    if (!source || typeof source !== 'object') return createNewLayerState().performanceMetrics.thresholdLevel;

    const thresholdLevel = source.thresholdLevel ?? createNewLayerState().performanceMetrics.thresholdLevel;

    if (thresholdLevel === null || typeof thresholdLevel === 'number') return thresholdLevel;
    if (Array.isArray(thresholdLevel) && thresholdLevel.length === 2 && thresholdLevel.every((entry) => typeof entry === 'number')) {
        return [thresholdLevel[0], thresholdLevel[1]];
    }
    return createNewLayerState().performanceMetrics.thresholdLevel;
}

function cleanupOldLayers(layers: Record<string, LayerState> | undefined) {
    const currentLayerRefs = getCurrentLayerRefs();

    if (!layers) {
        return { cleanedLayers: {} as Record<string, LayerState>, removedLayerCount: 0, removedIds: [] as string[] };
    }
    if (!currentLayerRefs) {
        return { cleanedLayers: layers, removedLayerCount: 0, removedIds: [] as string[] };
    }

    const validLayerKeys = new Set(currentLayerRefs.map((entry) => `${entry.geodataId}::${entry.layerId}`));
    const geodataIdsByLayerId = currentLayerRefs.reduce((acc, entry) => {
        const ids = acc.get(entry.layerId) ?? new Set<string>();
        ids.add(entry.geodataId);
        acc.set(entry.layerId, ids);
        return acc;
    }, new Map<string, Set<string>>());

    const filteredEntries = Object.entries(layers).flatMap(([id, layer]) => {
        if (id === 'mixed') return [[id, layer] as const];

        const geodataId = layer?.geodataId ?? '';
        if (geodataId) {
            return validLayerKeys.has(`${geodataId}::${id}`) ? ([[id, layer]] as const) : [];
        }

        const matchedGeodataIds = geodataIdsByLayerId.get(id);
        if (!matchedGeodataIds || matchedGeodataIds.size !== 1) {
            return [];
        }

        const [resolvedGeodataId] = matchedGeodataIds;
        return [[id, { ...layer, geodataId: resolvedGeodataId }]] as const;
    });

    const cleanedLayers = Object.fromEntries(filteredEntries) as Record<string, LayerState>;
    const removedIds = Object.keys(layers).filter((id) => !(id in cleanedLayers));
    return { cleanedLayers, removedLayerCount: removedIds.length, removedIds };
}

function cleanupMixerElements(layers: Record<string, LayerState>) {
    const mixed = layers.mixed;
    if (!mixed?.mixerElements) {
        return { layers, removedCount: 0 };
    }

    const filtered = mixed.mixerElements.filter((el) => Object.prototype.hasOwnProperty.call(layers, el.layerId));
    const removedCount = mixed.mixerElements.length - filtered.length;
    if (removedCount === 0) {
        return { layers, removedCount: 0 };
    }

    return {
        layers: {
            ...layers,
            mixed: {
                ...mixed,
                mixerElements: filtered,
            },
        },
        removedCount,
    };
}

function cleanupActiveLayer(activeLayerID: string | undefined, layers: Record<string, LayerState>) {
    const hasActive = activeLayerID && Object.prototype.hasOwnProperty.call(layers, activeLayerID);
    if (!activeLayerID || hasActive) {
        return { activeLayerID: activeLayerID ?? '', wasCleared: false };
    }

    return { activeLayerID: '', wasCleared: true };
}

function cleanupFailedLayers(failedLayers: Record<string, string> | undefined): Record<string, string> {
    if (!failedLayers) return {};
    const resource = getResourceStore();
    const gridResources = resource.status === 'loaded' ? resource.gridResources : null;
    const currentLayers: string[] | undefined = gridResources?.geodatas?.flatMap((geodata) =>
        geodata.simulations_sets.flatMap((simulations_set) =>
            simulations_set.simulations.map((subSimulation) => `${simulations_set.id}-${subSimulation.id}`)
        )
    );
    if (!currentLayers) return failedLayers;
    const validLayerIds = new Set(currentLayers);
    const filtered = Object.entries(failedLayers).filter(([id]) => validLayerIds.has(id));
    return Object.fromEntries(filtered);
}
